import { APITypes } from '../primary-record-types';
import useIsTestUser from './useIsTestUser';
import usePersistedKeyValueStore from './usePersistedKeyValueStore';
import { useEnvironment } from './useEnvironment';

export type UseFastenSandboxModeReturnValue = {
    isSandboxMode: boolean;
    canUseSandboxMode: boolean;
    /** True while the stored preference is still being read; callers should not send a mode yet. */
    isLoading: boolean;
    setIsSandboxMode: (value: boolean) => void;
    /** Sent on Fasten connect and catalog-search requests; undefined means the field is omitted (live). */
    apiMode: APITypes.ehrIntegrationsService.FastenApiMode | undefined;
};

/**
 * Staff-only switch that asks the backend to use Fasten's sandbox (test-mode) credentials for new portal
 * connections. Only offered while the app points at the dev backend (prod is live-only). The backend re-checks who
 * is asking and ignores the request in prod, so this is a preference the client sends along, not a permission it
 * holds.
 */
export default function useFastenSandboxMode(): UseFastenSandboxModeReturnValue {
    const isTestUser = useIsTestUser();
    const environment = useEnvironment();
    const { value, setValue, isLoading } = usePersistedKeyValueStore<boolean>({
        category: 'fasten',
        key: 'sandboxMode',
        defaultValue: false,
    });

    const canUseSandboxMode = isTestUser && environment === 'dev';
    const isSandboxMode = canUseSandboxMode && !!value;

    return {
        isSandboxMode,
        canUseSandboxMode,
        isLoading: canUseSandboxMode && isLoading,
        setIsSandboxMode: (next: boolean) => {
            void setValue(next);
        },
        apiMode: isSandboxMode ? 'test' : undefined,
    };
}
